import React from 'react';

const JsonSchemaFormObjectFieldTemplate = ({ TitleField, properties, title, description, ...rest }) => {
    let { schema: { root } } = rest;

    const renderField = (prop) => {
        const uiSchema = prop.content.props.uiSchema || {};

        if (uiSchema['ui:widget'] === 'hidden') {
            return (
                <React.Fragment key={prop.content.key}>
                    {prop.content}
                </React.Fragment>
            );
        }

        const hideLabel = uiSchema['ui:label'] === false;

        if (hideLabel && prop.content.props.schema && prop.content.props.schema.type !== 'null') {
            const content = React.cloneElement(prop.content, {
                ...prop.content.props,
                label: ''
            });
            prop = { ...prop, content };
        }

        const hideFieldErrors = uiSchema['ui:field'] === 'financialBreakdownValidation'
            || (uiSchema.wrapperClassNames || '').includes('financial-percentage-col')
            || (uiSchema.wrapperClassNames || '').includes('financial-amount-col')
            || (uiSchema.wrapperClassNames || '').includes('financial-table-col-value')
            || (uiSchema.wrapperClassNames || '').includes('financial-risk-input');

        if (uiSchema.classNames && uiSchema.classNames.indexOf('col') > -1) {
            return <div
                className={`object-field-wrapper ${uiSchema.wrapperClassNames || 'col col-12'}`}
                key={prop.content.key}>
                <div className="row">
                    {prop.description}
                    {prop.content}
                    {!hideFieldErrors && prop.errors}
                    {prop.help}
                </div>
            </div>
        } else {
            return <div
                className={`object-field-wrapper ${uiSchema.wrapperClassNames || 'col col-12'}`}
                key={prop.content.key}>
                {prop.description}
                {prop.content}
                {!hideFieldErrors && prop.errors}
                {prop.help}
            </div>
        }
    }

    const getFinancialRowSize = (fields, index) => {
        const prop = fields[index];
        const uiSchema = prop.content.props.uiSchema || {};
        const isRowLabel = prop.name.endsWith('RowLabel') || uiSchema['ui:field'] === 'financialTableRowLabel';

        if (!isRowLabel) {
            return null;
        }

        const wrapper = uiSchema.wrapperClassNames || '';

        if (wrapper.includes('financial-row-label')) {
            return 3;
        }

        if (wrapper.includes('financial-assets-liabilities')) {
            return 3;
        }

        if (wrapper.includes('financial-table-row-label') || uiSchema['ui:field'] === 'financialTableRowLabel') {
            const nextField = fields[index + 1];
            const nextWrapper = nextField
                ? (nextField.content.props.uiSchema || {}).wrapperClassNames || ''
                : '';

            if (nextWrapper.includes('col-md-7') || nextWrapper.includes('financial-risk-input')) {
                return 2;
            }
        }

        return null;
    };

    const renderFinancialColumnHeaders = (prop, uiSchema) => {
        const columns = uiSchema.columns || [];

        return (
            <div className="row financial-grid-row" key={prop.content.key + '-header'}>
                <div className="col-12 col-md-4 financial-grid-label-cell">&#160;</div>
                {columns.map((column) => (
                    <div
                        key={column}
                        className="col-12 col-md-4 financial-grid-header-cell"
                    >
                        <strong>{column}</strong>
                    </div>
                ))}
            </div>
        );
    };

    const renderAssetsLiabilitiesTableHeader = (prop) => (
        <div className="row financial-table-header-row" key={prop.content.key + '-header'}>
            <div className="col-12 financial-table-item-col financial-table-col-item">Item</div>
            <div className="col-12 financial-table-value-col financial-table-col-value">Current Value</div>
            <div className="col-12 financial-table-notes-col financial-table-col-notes">Notes</div>
        </div>
    );

    const isSummaryRow = (rowFields) => rowFields.some((rowProp) => {
        const wrapper = (rowProp.content.props.uiSchema || {}).wrapperClassNames || '';
        return wrapper.includes('financial-summary-row');
    });

    const renderFinancialFields = (fields) => {
        const result = [];
        let index = 0;

        while (index < fields.length) {
            const prop = fields[index];
            const uiSchema = prop.content.props.uiSchema || {};

            if (uiSchema['ui:widget'] === 'hidden') {
                result.push(renderField(prop));
                index += 1;
                continue;
            }

            if (uiSchema['ui:field'] === 'financialColumnHeaders') {
                result.push(renderFinancialColumnHeaders(prop, uiSchema));
                index += 1;
                continue;
            }

            if (uiSchema['ui:field'] === 'assetsLiabilitiesTableHeader') {
                result.push(renderAssetsLiabilitiesTableHeader(prop));
                index += 1;
                continue;
            }

            const rowSize = getFinancialRowSize(fields, index);

            if (rowSize && index + rowSize <= fields.length) {
                const rowFields = fields.slice(index, index + rowSize);
                const rowClassName = [
                    'row',
                    'financial-grid-row',
                    isSummaryRow(rowFields) ? 'financial-summary-row' : '',
                ].filter(Boolean).join(' ');
                result.push(
                    <div className={rowClassName} key={prop.content.key + '-row'}>
                        {rowFields.map((rowProp) => renderField(rowProp))}
                    </div>
                );
                index += rowSize;
                continue;
            }

            result.push(renderField(prop));
            index += 1;
        }

        return result;
    };

    const groupPropertiesIntoSections = (props) => {
        const groups = [];
        let currentSection = null;

        props.forEach((prop) => {
            const uiSchema = prop.content.props.uiSchema || {};
            const fieldName = prop.name;

            if (uiSchema.header || fieldName === 'legal') {
                if (currentSection) {
                    groups.push(currentSection);
                    currentSection = null;
                }
            }

            if (uiSchema.sectionMeta) {
                if (currentSection) {
                    groups.push(currentSection);
                }
                currentSection = {
                    meta: uiSchema.sectionMeta,
                    fields: [prop]
                };
            } else if (currentSection) {
                currentSection.fields.push(prop);
            } else {
                groups.push({ meta: null, fields: [prop] });
            }
        });

        if (currentSection) {
            groups.push(currentSection);
        }

        return groups;
    }

    const renderSection = (section, key) => {
        if (!section.meta) {
            return section.fields.map((prop) => renderField(prop));
        }

        const cardFields = section.fields.filter((prop) => !prop.content.props.uiSchema.outsideCard);
        const footerFields = section.fields.filter((prop) => prop.content.props.uiSchema.outsideCard);

        return (
            <div className={`col-12 financial-section financial-section-${section.meta.number}`} key={key}>
                <div className="financial-section-header">
                    <span className="financial-section-number">{section.meta.number}.</span>
                    <span className="financial-section-title">{section.meta.title}</span>
                </div>
                <div className="financial-section-card">
                    <div className="financial-section-body">
                        {renderFinancialFields(cardFields)}
                    </div>
                </div>
                {footerFields.length > 0 && (
                    <div className="financial-section-footer">
                        {footerFields.map((prop) => renderField(prop))}
                    </div>
                )}
            </div>
        );
    }

    if (root === true) {
        const sections = groupPropertiesIntoSections(properties);

        return (
            <div>
                <TitleField title={title} className="object-field" />
                <div className={root === true ? 'row' : ''}>
                    {sections.map((section, index) => {
                        if (!section.meta) {
                            return section.fields.map((prop) => {
                                let content = [];
                                if (prop.content.props.uiSchema.header) {
                                    content.push(
                                        <div className="col col-12" key={prop.content.key + 'header'}>
                                            <div className=" alert alert-dark">
                                                {prop.content.props.uiSchema.header}
                                            </div>
                                        </div>
                                    )
                                }

                                if (prop.content.props.uiSchema.blockLabel) {
                                    content.push(
                                        <div className="col col-12" key={prop.content.key + 'blockLabel'}>
                                            <h5>
                                                {prop.content.props.uiSchema.blockLabel}
                                            </h5>
                                        </div>
                                    )
                                }

                                content.push(renderField(prop));
                                return content;
                            });
                        }

                        return renderSection(section, index);
                    })}
                </div>
                {description}
            </div>
        )
    } else {
        return properties.map((prop, key) => {
            let content = [];

            if (prop.content.props.uiSchema.header) {
                content.push(
                    <div className="col col-12" key={prop.content.key + 'header'}>
                        <div className="alert alert-dark">
                            <h3>{prop.content.props.uiSchema.header}</h3>
                        </div>
                    </div>
                )
            }

            content.push(
                renderField(prop)
            );

            return content;

        })
    }
}

export default JsonSchemaFormObjectFieldTemplate;
